import { ChangeEvent, FocusEvent, MouseEvent, ReactNode, SyntheticEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Menu, MenuItem } from '@mui/material'
import classNames from 'classnames'
import { isEmpty, isString, xor } from 'lodash'

import { Checkbox } from '../Checkbox'
import { Field } from '../Field'
import { Icon, IconName } from '../Icon'
import { TextStyle } from '../sharedTypes'
import Tag from '../Tag'
import { Text } from '../Text'

import './index.scss'

export type Option<T = string> = {
  key?: T
  text: string
  value?: T | undefined
  description?: string
  disabled?: boolean
}
type UIEvent = SyntheticEvent<HTMLElement, Event>

// Mirrors the menu's scss `max-height`; used to estimate whether the open menu
// fits below the trigger before deciding to flip it upward.
const MENU_MAX_HEIGHT = 260
// Approximate rendered item height (Semantic padding + text + divider).
const APPROX_ITEM_HEIGHT = 44

export interface DropdownProps {
  onChange: (value?: string | (string | undefined)[]) => void
  options: Option[]
  className?: string
  direction?: 'left' | 'right'
  disabled?: boolean
  error?: boolean
  name?: string
  onBlur?: (e?: FocusEvent<HTMLElement, Element>, data?: unknown) => void
  onFocus?: (e?: UIEvent, data?: unknown) => void
  placeholder?: string
  selectAllLabel?: string
  trigger?: ReactNode
  value?: string | string[] | undefined
  fullOptions?: Option[]
  titleText?: string
  id?: string
  halfSize?: boolean
  'data-testid'?: string
}

/** Data passed to a DropdownItem's onClick (mirrors semantic's `{ value }`). */
export interface DropdownItemData {
  value?: string
}

export interface DropdownItemProps {
  active?: boolean
  className?: string
  disabled?: boolean
  icon?: IconName
  onClick?: (event: MouseEvent, data: DropdownItemData) => void
  text?: string
  description?: string
  textStyle?: TextStyle
  value?: string
  'data-testid'?: string
}

interface TriggerProps {
  error?: boolean
  className?: string
  isOpen: boolean
  onChange: (value?: string | string[]) => void
  onItemClose: (value: string) => void
  options: Option[]
  placeholder?: string
  value?: string | string[]
  fullOptions?: Option[]
}

interface SelectAllProps {
  checked: boolean
  label: string
  onChange: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void
}

const Arrow = ({ up }: { up: boolean }) => (
  <Icon className="dropdown-arrow" color="black-1" icon={`Arrow${up ? 'Up' : 'Down'}`} />
)

const ClearAll = ({ onClick }: { onClick: (e: SyntheticEvent) => void }) => {
  const { t } = useTranslation()

  return (
    <div className="clear-all">
      <Text link onClick={onClick} styleType="link">
        {t('common.clearAll', 'clear all').toLocaleUpperCase()}
      </Text>
    </div>
  )
}

export const DropdownItem = ({
  active,
  className,
  disabled,
  icon,
  text,
  textStyle,
  description,
  value,
  onClick,
  'data-testid': dataTestId,
}: DropdownItemProps) => {
  return (
    <MenuItem
      className={classNames(
        'populus-dropdown-item',
        active && 'selected',
        disabled && 'disabled',
        className
      )}
      data-testid={dataTestId}
      disabled={disabled}
      disableRipple
      onClick={onClick ? event => onClick(event, { value }) : undefined}
    >
      <div className="flex items-center">
        {icon && <Icon className="item-icon" color="black-0" icon={icon} />}

        <Text
          className={classNames('dropdown-item-text', icon && 'icon-text')}
          styleType={textStyle ? textStyle : icon ? 'button-light' : 'body'}
        >
          {text}
        </Text>
      </div>
      {description && (
        <Text
          className={classNames('dropdown-item-text', icon && 'icon-text', 'description-text')}
          styleType={textStyle ? textStyle : icon ? 'button-light' : 'body-light'}
        >
          {description}
        </Text>
      )}
      {active ? <Icon className="item-checkmark" color="ocean-1" icon="Checkmark" /> : null}
    </MenuItem>
  )
}

const Trigger = ({
  isOpen,
  className,
  onChange,
  onItemClose,
  options,
  placeholder,
  value,
  fullOptions,
}: TriggerProps) => {
  const showTextValue = isString(value) || isEmpty(value)
  const textValue = (
    <Text styleType="body">
      {isString(value) ? activeText(fullOptions ? fullOptions : options, value) : placeholder}
    </Text>
  )
  const multipleValues = (
    <div className="multiple-values-container">
      {Array.isArray(value) &&
        value.map(val => (
          <Tag
            key={val}
            onClick={(e: SyntheticEvent) => e.stopPropagation()}
            onClose={() => onItemClose(val)}
            text={activeText(fullOptions ? fullOptions : options, val)}
          />
        ))}
    </div>
  )
  const clearAll = (
    <ClearAll
      onClick={(e: SyntheticEvent) => {
        e.stopPropagation()
        onChange([])
      }}
    />
  )
  return (
    <div
      className={classNames(
        'populus-dropdown-trigger',
        !showTextValue && (value as string[]).length > 1 && 'show-clear-all',
        className
      )}
    >
      {showTextValue ? textValue : multipleValues}
      <Arrow up={isOpen} />
      {!showTextValue && (value as string[]).length > 1 && clearAll}
    </div>
  )
}

const SelectAll = ({ checked, label, onChange }: SelectAllProps) => {
  return (
    <Checkbox
      checked={checked}
      className="populus-dropdown-checkbox"
      label={label}
      onChange={onChange}
    />
  )
}

export const Dropdown = ({
  onChange,
  options,
  className,
  direction,
  disabled,
  error,
  onBlur,
  placeholder,
  selectAllLabel,
  trigger,
  value,
  fullOptions,
  titleText,
  halfSize,
  id,
  'data-testid': dataTestId,
}: DropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [openUpward, setOpenUpward] = useState(false)
  const isOpen = Boolean(anchorEl)
  const isMultiple = Array.isArray(value)

  const openMenu = (event: UIEvent) => {
    if (disabled) return
    // Semantic auto-flipped the menu upward when it would overflow the viewport;
    // MUI's Menu does not, so decide the vertical direction ourselves on open.
    const rect = event.currentTarget.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const estimatedHeight = Math.min(MENU_MAX_HEIGHT, options.length * APPROX_ITEM_HEIGHT)
    setOpenUpward(spaceBelow < estimatedHeight && rect.top > spaceBelow)
    setAnchorEl(event.currentTarget)
  }

  const closeMenu = () => {
    setAnchorEl(null)
    onBlur?.()
  }

  const removeMultipleValue = (val: string) => onChange((value as string[]).filter(v => v !== val))

  const addMultipleValue = (val: string) => onChange([...(value as string[]), val])

  const onItemClick = (event: MouseEvent, data: DropdownItemData) => {
    const val = data.value
    if (val === undefined) return
    if (isMultiple) {
      // keep the menu open while toggling individual values
      event.stopPropagation()
      ;(value as string[]).includes(val) ? removeMultipleValue(val) : addMultipleValue(val)
    } else {
      onChange(val)
      setAnchorEl(null)
    }
  }

  const allOptions = options
    .map(option => option.value)
    .filter((value): value is string => value !== undefined)

  const onSelectAllChange = (_e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) onChange(allOptions)
    else onChange([])
  }

  // semantic `direction="left"` opened the menu toward the trigger's left edge
  const horizontal = direction === 'left' ? 'right' : 'left'

  const dropdown = (
    <>
      <div
        aria-expanded={isOpen}
        className={classNames(
          'populus-dropdown',
          halfSize && 'populus-dropdown-half',
          error && 'error',
          className
        )}
        aria-disabled={disabled}
        data-testid={dataTestId}
        id={id}
        onClick={openMenu}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openMenu(event)
          }
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        {trigger || (
          <Trigger
            className={className}
            isOpen={isOpen}
            onChange={onChange}
            onItemClose={removeMultipleValue}
            options={options}
            placeholder={placeholder}
            value={value}
            fullOptions={fullOptions}
          />
        )}
      </div>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: openUpward ? 'top' : 'bottom', horizontal }}
        disableAutoFocusItem
        onClose={closeMenu}
        open={isOpen}
        slotProps={{
          list: {
            className: classNames(
              'populus-dropdown-menu',
              halfSize && 'populus-dropdown-menu-half'
            ),
            disablePadding: true,
          },
          paper: {
            className: classNames('populus-dropdown-menu-paper', openUpward && 'open-upward'),
            elevation: 0,
            square: true,
          },
        }}
        transformOrigin={{ vertical: openUpward ? 'bottom' : 'top', horizontal }}
      >
        {options?.map(({ description, text, value: optionValue, ...optionProps }) => (
          <DropdownItem
            active={
              isMultiple ? (value as string[]).includes(optionValue || '') : optionValue === value
            }
            key={optionValue}
            onClick={onItemClick}
            description={description}
            text={text}
            value={optionValue}
            {...optionProps}
          />
        ))}
      </Menu>
      {selectAllLabel && (
        <SelectAll
          checked={xor(allOptions, value).length === 0}
          label={selectAllLabel}
          onChange={onSelectAllChange}
        />
      )}
    </>
  )
  return titleText ? <Field titleText={titleText}> {dropdown}</Field> : dropdown
}

function activeText(options: Option[], value?: string) {
  return options?.find(option => option.value === value)?.text
}
